// Imported first so a missing runtime config is reported before any module reads it.
import { getConfigProblems } from 'utils/diagnostics/bootstrap';
import { renderConfigErrorScreen } from 'utils/diagnostics/renderConfigErrorScreen';
import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { App } from 'pages/App';
import Routes from 'pages/Routes';
import { QueryClient, QueryClientProvider } from 'react-query';
import * as axiosClient from 'utils/api/axiosClient';
import { configureStore } from './store';
import {
  ErrorProvider,
  useErrorContext,
} from 'components/Patient/context/ErrorContext';
import { GlobalErrorAlert } from 'components/Modals/ConfirmationAlertsModal/GlobalErrorAlert';

import {
  API_URL,
  API_URL_FOR_LISTS,
  DMS_API_URL,
  SENTRY_DSN,
  BUILD_NUMBER,
  SENTRY_ENV,
  SENTRY_TRACES_SAMPLE_RATE,
  SENTRY_REPLAYS_ON_ERROR_RATE,
} from 'config/constants';

import './styles/index.scss';
import './styles/dms-global.scss';
import { Toaster } from 'components/Toaster';

try {
  if (!!SENTRY_DSN) {
    Sentry.init({
      dsn: SENTRY_DSN,
      release: BUILD_NUMBER,
      environment: SENTRY_ENV,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: SENTRY_REPLAYS_ON_ERROR_RATE,
      tracePropagationTargets: [API_URL, API_URL_FOR_LISTS],
    });
  }
} catch (e) {
  console.error('Unable to initialize Sentry');
  console.error(e);
}

export const MOUNT_NODE = document.getElementById('root');

const initialState = {};
const store = configureStore(initialState);

if (process.env.NODE_ENV === 'development') {
  window.__REDUX_STORE__ = store;
}

const queryClient = new QueryClient();

const AxiosErrorInterceptorConnector = () => {
  const { setErrorMessageAlert, errorMessages } = useErrorContext();

  useEffect(() => {
    const unsubscribe = axiosClient.subscribeToErrors(setErrorMessageAlert);
    return () => unsubscribe();
  }, [setErrorMessageAlert]);

  useEffect(() => {
    if (errorMessages === null) {
      axiosClient.getAxiosWrapper().hasShownUnauthorizedAlert = false;
    }
  }, [errorMessages]);

  return null;
};

axiosClient.init({
  store,
  API_URL,
  API_URL_FOR_LISTS,
  DMS_API_URL,
});

if (!MOUNT_NODE) {
  throw new Error('Root element #root not found');
}

const configProblems = getConfigProblems();

if (configProblems.length > 0) {
  renderConfigErrorScreen(MOUNT_NODE, configProblems);
  throw new Error(
    `Anova was not started: ${configProblems.length} configuration problem(s). See the console for details.`,
  );
}

createRoot(MOUNT_NODE).render(
  <>
    <Provider store={store}>
      <ErrorProvider>
        <AxiosErrorInterceptorConnector />
        <Toaster />
        <GlobalErrorAlert />
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Helmet defaultTitle="Anova">
              <meta
                name="description"
                content="Anova OneSource web application."
              />
            </Helmet>
            <ErrorBoundary>
              <App>
                <Routes />
              </App>
            </ErrorBoundary>
          </BrowserRouter>
        </QueryClientProvider>
      </ErrorProvider>
    </Provider>
  </>,
);
